import React, { useEffect, useRef, useState } from "react";

type Particle = {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  color: string;
  spin: number;
  angle: number;
  settled: boolean;
  life: number;
  restFrames: number;
};

const SECRET: string[] = ["c", "o", "n", "f"];
const COLORS = ["#f44336", "#ffb300", "#ffd54f", "#039be5"];
const DURATION_MS = 10000;

export function ConfettiEasterEgg() {
  const [active, setActive] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const bufferRef = useRef<string[]>([]);
  const stopTimeoutRef = useRef<number | null>(null);
  const spawnTimeoutRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const lastTickRef = useRef<number>(0);

  // Listen for key sequence to trigger confetti
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      bufferRef.current.push(key);
      if (bufferRef.current.length > SECRET.length) bufferRef.current.shift();
      const offset = bufferRef.current.length - SECRET.length;
      if (offset >= 0) {
        const match = SECRET.every((k, i) => bufferRef.current[offset + i] === k);
        if (match && !active) setActive(true);
      }
      if (active && key === "escape") setStopRequested(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active]);

  // When stop is requested, halt future spawns and ensure timer at 0
  useEffect(() => {
    if (stopRequested) {
      if (spawnTimeoutRef.current !== null) {
        window.clearTimeout(spawnTimeoutRef.current);
        spawnTimeoutRef.current = null;
      }
    }
  }, [stopRequested]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active) return;
    setStopRequested(false);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    particlesRef.current = [];

    const spawn = () => {
      if (stopRequested) return;
      const count = 12;
      for (let i = 0; i < count; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: -10,
          r: 6 + Math.random() * 10,
          vx: (Math.random() - 0.5) * 1.4,
          vy: 1.5 + Math.random() * 2.5,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          spin: (Math.random() - 0.5) * 0.1,
          angle: Math.random() * Math.PI * 2,
          settled: false,
          life: 320,
          restFrames: 0,
        });
      }
      spawnTimeoutRef.current = window.setTimeout(spawn, 300);
    };
    spawn();

    const step = (ts: number) => {
      if (!ctx) return;
      if (lastTickRef.current === 0) lastTickRef.current = ts;
      const dt = ts - lastTickRef.current;
      lastTickRef.current = ts;
      if (!stopRequested && ts >= DURATION_MS) setStopRequested(true);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.angle += p.spin;
        if (!p.settled) {
          p.vx *= 0.99;
          p.vy = Math.min(p.vy + 0.04, 4.2);
          const floor = canvas.height - 12;
          if (p.y + p.r >= floor) {
            p.y = floor - p.r;
            if (Math.abs(p.vy) < 0.35) {
              p.vy = 0;
              p.vx *= 0.9;
              p.settled = Math.abs(p.vx) < 0.15;
            } else {
              p.vy = -Math.abs(p.vy) * 0.45;
              p.vx *= 0.95;
            }
          }
        } else {
          p.restFrames += 1;
          // After resting a bit, drift downward off-screen
          if (p.restFrames > 60) {
            p.vy = Math.min(p.vy + 0.04, 2.2);
            p.y += p.vy;
            p.vx *= 0.98;
          }
        }
        // Fade/slide away when stopping
        if (stopRequested) {
          if (p.settled) {
            p.vx *= 0.98;
            p.vy = Math.min(p.vy + 0.02, 1.2);
          }
          p.life -= 2;
          p.r = Math.max(0, p.r - 0.05);
        } else {
          p.life -= 0.5;
        }
      });
      particlesRef.current = particlesRef.current.filter(
        (p) => p.r > 0.2 && p.life > 0 && p.y - p.r < canvas.height + 120
      );
      ctx.save();
      particlesRef.current.forEach((p) => {
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(0, 0, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      });
      ctx.restore();
      if (stopRequested && particlesRef.current.length === 0) {
        setActive(false);
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    stopTimeoutRef.current = window.setTimeout(() => setStopRequested(true), DURATION_MS);

    // If stop requested, clear future spawns
    if (stopRequested && spawnTimeoutRef.current !== null) {
      window.clearTimeout(spawnTimeoutRef.current);
      spawnTimeoutRef.current = null;
    }

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (spawnTimeoutRef.current !== null) window.clearTimeout(spawnTimeoutRef.current);
      if (stopTimeoutRef.current !== null) window.clearTimeout(stopTimeoutRef.current);
      window.removeEventListener("resize", resize);
      particlesRef.current = [];
    };
  }, [active]);

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 9998,
          display: active ? "block" : "none",
        }}
      />
    </>
  );
}
